import styled from 'styled-components';
import React, { useMemo } from 'react';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { FaChevronLeft, FaRegStar, FaStar } from 'react-icons/fa';
import { useImageThumbnailGallery } from '@/features/shared/experience-info-modal/use-image-thumbnail-gallery';
import {
  ImageEditorContext,
  newImageEditorContext,
} from '@/features/editor/widgets/custom-widget/inputs/background/image/shared/context';
import { ImageEditor } from '@/features/editor/widgets/custom-widget/inputs/background/image/editor';
import Portal from '@/components/portal';
import { PopConfirm } from '@/components/pop-confirm';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import { breakpoints } from '@/components/responsive';

export interface PreviewImageVerticalGalleryProps {
  initialValue?: string[];
  maxImages?: number;
  allowUpload?: boolean;
  onImageListChanged?: (list: string[], id?: string) => void;
  isFavoriteStarUpdate?: boolean;
  favoriteImage?: string | null | undefined;
}

export function PreviewImageVerticalGallery({
  onImageListChanged,
  initialValue,
  maxImages,
  allowUpload,
  isFavoriteStarUpdate,
  favoriteImage,
}: PreviewImageVerticalGalleryProps) {
  const ctx = newImageEditorContext(``);
  const { image: uploadedImage, setImage: setUploadedImage } = ctx;

  const {
    loading,
    getRootProps,
    getInputProps,
    style,
    onCancelUpload,
    onUploadBlob,
    imageList,
    imageCount,
    maxImagesToShow,
    isSliderOpen,
    setIsSliderOpen,
    sliderImage,
    onImageClicked,
    onImageDelete,
    onFavoriteClicked,
  } = useImageThumbnailGallery(
    maxImages,
    uploadedImage,
    setUploadedImage,
    onImageListChanged,
    initialValue,
  );

  const isFavoriteExists = useMemo(
    () => (favoriteImage ? imageList?.includes(favoriteImage) : false),
    [favoriteImage, imageList],
  );

  const orderedImages = useMemo(() => {
    if (isFavoriteExists) {
      return [
        favoriteImage,
        ...imageList.filter((img) => img !== favoriteImage),
      ];
    }
    return imageList;
  }, [imageList, favoriteImage]);

  const updateFavorite = (
    img: string,
    e: React.MouseEvent<SVGElement | HTMLElement>,
  ) => {
    const target = e.currentTarget as SVGElement | HTMLElement | null;

    const targetElem = target?.closest(
      `.thumbnail-wrapper`,
    ) as HTMLElement | null;
    const wrapper = targetElem?.closest(
      `.vertical-wrapper`,
    ) as HTMLElement | null;

    if (!targetElem || !wrapper) {
      onFavoriteClicked(img);
      return;
    }
    if (targetElem && wrapper) {
      const ANIMATION_DURATION_MS = 250;
      const FAVORITE_UPDATE_DELAY_MS = 550;

      const top = targetElem.offsetTop;

      wrapper.classList.add(`animation-ongoing`);
      targetElem.style.transition = `transform ${ANIMATION_DURATION_MS}ms ease`;
      targetElem.style.transform = `translateY(-${top}px)`;
      targetElem.style.opacity = `1`;
      wrapper.scrollTo({
        top: 0,
        behavior: `auto`,
      });

      setTimeout(() => {
        onFavoriteClicked(img);
        wrapper.classList.remove(`animation-ongoing`);
        targetElem.style.opacity = null;
        targetElem.style.transform = null;
        targetElem.style.transition = null;
      }, FAVORITE_UPDATE_DELAY_MS);
    }
  };

  return (
    <ImageEditorContext.Provider value={ctx}>
      <Wrapper
        className="vertical-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        {!!uploadedImage && (
          <Portal selector="#shared-elem">
            <OverlayWrapper>
              <BackdropSpinner loading={loading} />
              <DiscardUploadButton onClick={onCancelUpload}>
                Cancel
              </DiscardUploadButton>
              <ImageEditor buttonText="Upload" onUpdatePreview={onUploadBlob} />
            </OverlayWrapper>
          </Portal>
        )}
        {isSliderOpen && (
          <Portal selector="body">
            <ImageZoomWrapper onClick={() => setIsSliderOpen(false)}>
              <BackButtonWrapper>
                <FaChevronLeft color="#979a9e" size={25} />
              </BackButtonWrapper>
              <div className="img-container">
                <img src={sliderImage} alt={sliderImage} />
              </div>
            </ImageZoomWrapper>
          </Portal>
        )}
        {orderedImages?.map((img, index) => (
          <ThumbnailWrapper className="thumbnail-wrapper" key={img}>
            <div className="delete-image">
              <PopConfirm
                payload={img}
                onConfirm={onImageDelete}
                text="Are you sure you want to delete this image?"
              >
                <RiDeleteBin5Line />
              </PopConfirm>
            </div>
            {isFavoriteStarUpdate && imageList.length > 1 && (
              <div
                className={`star-image ${
                  !isFavoriteExists && index === 0 ? `default-favorite` : ``
                }`}
              >
                {img === favoriteImage || (!isFavoriteExists && index === 0) ? (
                  <FaStar
                    onClick={() => {
                      onFavoriteClicked(null);
                    }}
                  />
                ) : (
                  <FaRegStar
                    onClick={(e) => {
                      updateFavorite(img, e);
                    }}
                  />
                )}
              </div>
            )}
            <Image
              isFavoriteStarUpdate={isFavoriteStarUpdate}
              onClick={() => onImageClicked(img)}
              src={img}
            />
          </ThumbnailWrapper>
        ))}
        {allowUpload && imageCount < maxImagesToShow && (
          <StyledDropzone {...getRootProps({ style })}>
            <input {...getInputProps()} />
          </StyledDropzone>
        )}
      </Wrapper>
    </ImageEditorContext.Provider>
  );
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  overflow-y: scroll;
  padding: 0 10px 5rem;
  width: 100%;
  height: 100%;

  &.animation-ongoing .thumbnail-wrapper {
    opacity: 0.25;
    transition: opacity 0.2s;
  }
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 35px;
  transform: translateY(0px);

  &&:hover {
    .delete-image,
    .star-image {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .delete-image,
  .star-image {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out, pointer-events 0.2s ease-in-out;
    transition-delay: 0.5s;
    cursor: context-menu;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    font-size: 2.5rem;
    color: black;
    width: 4rem;
    height: 4rem;
    padding-top: 5px;
    border-radius: 100%;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: rgba(100, 100, 111, 0.2) 0 7px 29px 0;
  }

  .star-image {
    top: 6.5rem;
    padding-top: 0;
  }

  .default-favorite {
    pointer-events: none !important;
    cursor: default !important;
  }
`;

const Image = styled.img`
  cursor: zoom-in;
  max-width: 100%;
  height: auto;
  transition: opacity 0.2s ease;
  box-shadow: rgba(0, 0, 0, 0.17) 0 3px 8px;
  border-radius: 35px;
  max-height: ${({ isFavoriteStarUpdate }) =>
    isFavoriteStarUpdate
      ? `calc(100dvh - 3rem - 45px - 3rem - 15px - 3rem - 3rem - 100px - 3rem)`
      : null};

  ${breakpoints.down(`md`)} {
    max-height: 55dvh;
  }
`;

const OverlayWrapper = styled.div`
  z-index: 100000000000;
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
`;

const DiscardUploadButton = styled.button`
  position: absolute;
  appearance: none;
  outline: none;
  border: 1px solid gray;
  top: 1.18rem;
  right: 9rem;
  z-index: 10;
  border-radius: 2rem;
  padding: 0.5rem 1rem;
  font-size: 1.3rem;
  cursor: pointer;

  &&:hover {
    opacity: 0.8;
  }

  &&:active {
    opacity: 0.6;
  }
`;

const StyledDropzone = styled.div`
  border-radius: 24px;
  border: dashed 3px #979797;
  background-color: #d8d8d8;
  position: relative;
  cursor: pointer;
  min-height: 100px;
  width: 100%;
  opacity: 0.4;
  transform: unset !important;

  &&:hover {
    opacity: 0.6;
    text-decoration: underline;
  }

  &:hover::after {
    text-decoration: underline;
  }

  &&:after {
    position: absolute;
    height: 100%;
    width: 100%;
    content: 'Drag & Drop or click to add an image';
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #0b0d0e;
  }
`;

const ImageZoomWrapper = styled.div`
  position: fixed;
  inset: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  z-index: 1000000000000000000;
  background: rgba(238, 238, 238, 0.76);
  backdrop-filter: blur(20px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;

  && * {
    user-select: none;
  }

  .img-container {
    padding: 0;
    margin: 0;
    position: relative;
    height: auto;
    width: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 90vh;
    max-width: 90vw;
  }

  img {
    display: flex;
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-height: 90vh;
    max-width: 90vw;
    border-radius: 40px;
    box-shadow: rgba(0, 0, 0, 0.35) 0 10px 15px;
  }
`;

const BackButtonWrapper = styled.div`
  position: absolute;
  top: 3rem;
  left: 3rem;
  background: white;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  box-shadow: rgba(100, 100, 111, 0.35) 0 7px 29px 0;
  cursor: pointer;
  width: 50px;
  height: 50px;
`;
